/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { memo } from 'react';

import { css } from '@compiled/react';

import { cssMap, cx, jsx } from '@atlaskit/css';
import __noop from '@atlaskit/ds-lib/noop';
import { fg } from '@atlaskit/platform-feature-flags';
import { Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useBreadcrumbsSize } from './internal/use-breadcrumbs-size';

const noop = __noop;

const itemWrapperStyles = css({
	display: 'flex',
	boxSizing: 'border-box',
	maxWidth: '100%',
	height: `${24 / 14}em`,
	flexDirection: 'row',
	marginBlockEnd: token('space.0'),
	marginBlockStart: token('space.0'),
	marginInlineEnd: token('space.0'),
	marginInlineStart: token('space.0'),
	paddingBlockEnd: token('space.0'),
	paddingBlockStart: token('space.0'),
	paddingInlineEnd: token('space.0'),
	paddingInlineStart: token('space.0'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:not(:last-child)::after': {
		width: token('space.100'),
		flexShrink: 0,
		color: token('color.text.subtlest'),
		content: '"/"',
		paddingBlock: token('space.025'),
		paddingInline: token('space.100'),
		textAlign: 'center',
	},
});

const styles = cssMap({
	root: {
		font: token('font.body'),
		backgroundColor: token('color.background.neutral.subtle'),
		padding: '0',
		color: token('color.text.subtlest'),
		border: 'none',
		display: 'inline-flex',
		alignItems: 'center',
		alignSelf: 'center',
		gap: token('space.050'),
		borderRadius: token('radius.small'),
		textDecoration: 'none',
		marginBlockStart: token('space.0'),

		'&:hover': {
			textDecoration: 'underline',
			color: token('color.text.subtlest'),
		},
		'&:focus': {
			textDecoration: 'none',
			color: token('color.text.subtlest'),
		},
		'&:active': {
			// @ts-expect-error
			color: token('color.text'),
		},
	},
	rootSmall: {
		font: token('font.body.small'),
	},
	interactiveMotion: {
		textDecorationLine: 'underline',
		textDecorationColor: 'transparent',
		transition: token('motion.listitem.hovered'),
		'&:hover': {
			textDecorationColor: token('color.text.subtlest'),
			transition: token('motion.listitem.pressed'),
		},
		'&:active': {
			transition: token('motion.listitem.pressed'),
			textDecorationColor: token('color.text'),
		},
	},
});

interface EllipsisItemProps {
	onClick?: (event: React.MouseEvent<Element>) => void;
	/**
	 * A `testId` prop is provided for specified elements, which is a unique string that appears as a data attribute `data-testid` in the rendered code, serving as a hook for automated tests.
	 */
	testId?: string;
	/**
	 * A `label` prop is used as aria-label for ellipsis button.
	 */
	label: string;
}

const EllipsisItem: import('react').MemoExoticComponent<
	({ label, onClick, testId }: EllipsisItemProps) => JSX.Element
> = memo(({ label, onClick = noop, testId }: EllipsisItemProps) => {
	const breadcrumbsSize = useBreadcrumbsSize();
	const isSmall = breadcrumbsSize === 'small';

	return (
		<li css={itemWrapperStyles}>
			<Pressable
				aria-label={label}
				onClick={onClick}
				xcss={cx(
					styles.root,
					isSmall && styles.rootSmall,
					fg('platform-dst-motion-uplift-list-item') && styles.interactiveMotion,
				)}
				testId={testId}
			>
				&hellip;
			</Pressable>
		</li>
	);
});

// eslint-disable-next-line @repo/internal/react/require-jsdoc
export default EllipsisItem;
